import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const TranslationDocuments = () => {
  usePageTitle("Translation Documents");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { setOverlayName, showSnackbar } = useStateContext();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [documentNameValues, setDocumentNameValues] = useState([""]);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [languages, setLanguages] = useState([]);

  const duplicateDocumentNameSet = useMemo(() => {
    const counts = new Map();
    documentNameValues.forEach((value) => {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return;
      counts.set(normalized, (counts.get(normalized) || 0) + 1);
    });

    const duplicates = new Set();
    counts.forEach((count, key) => {
      if (count > 1) {
        duplicates.add(key);
      }
    });
    return duplicates;
  }, [documentNameValues]);

  const hasDuplicateDocumentNames = duplicateDocumentNameSet.size > 0;

  const isLanguageField = useCallback((field) => {
    if (!field) return false;

    const typeMatch = String(field.FieldType || "").toLowerCase() === "language";
    const textMatch = `${field.OriginalPlaceholder || ""} ${field.UserFriendlyLabel || ""}`
      .toLowerCase()
      .includes("language");

    return typeMatch || textMatch;
  }, []);

  const isDocumentNameField = useCallback((field) => {
    if (!field) return false;
    const text = `${field.OriginalPlaceholder || ""} ${field.UserFriendlyLabel || ""}`.toLowerCase();
    return text.includes("document") && text.includes("name");
  }, []);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const [documentsResponse, languagesResponse] = await Promise.all([
          api.get("/serviceDocuments"),
          api.get("/languages"),
        ]);
        setDocuments(documentsResponse.data || []);
        const languageList = Array.isArray(languagesResponse.data)
          ? languagesResponse.data.filter((language) => language?.Name !== "ALL")
          : [];
        setLanguages(languageList);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to load documents");
        showSnackbar("Failed to load service documents", theme, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [showSnackbar, theme]);

  const closeOverlay = () => setOverlayName("");

  const initFieldValues = useCallback((fields) => {
    const today = new Date().toISOString().slice(0, 10);
    const values = {};
    (fields || []).forEach((f) => {
      if (f.FieldType === "boolean") {
        values[f.OriginalPlaceholder] = f.DefaultValue === "true" || f.DefaultValue === "yes" ? true : false;
      } else if (f.FieldType === "date") {
        values[f.OriginalPlaceholder] = today;
      } else if (isLanguageField(f)) {
        values[f.OriginalPlaceholder] = f.DefaultValue || "";
      } else {
        values[f.OriginalPlaceholder] = f.DefaultValue || "";
      }
    });
    return values;
  }, [isLanguageField]);

  const handleSelectDoc = async (doc) => {
    setLoadingDoc(true);
    try {
      const response = await api.get(`/serviceDocuments/${doc.id}`);
      const fullDoc = response.data;
      const sortedFields = [...(fullDoc.Fields || [])].sort(
        (a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0) || a.id - b.id,
      );
      fullDoc.Fields = sortedFields;
      setSelectedDoc(fullDoc);
      setFieldValues(initFieldValues(sortedFields));
      const documentNameField = sortedFields.find((f) => isDocumentNameField(f));
      const initialDocumentName = documentNameField ? String(documentNameField.DefaultValue || "") : "";
      setDocumentNameValues([initialDocumentName]);
    } catch {
      showSnackbar("Failed to load document fields", theme, "error");
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleBack = () => {
    setSelectedDoc(null);
    setFieldValues({});
    setDocumentNameValues([""]);
  };

  const handleDocumentNameValueChange = useCallback((index, newValue) => {
    setDocumentNameValues((prev) => prev.map((value, idx) => (idx === index ? newValue : value)));
  }, []);

  const addDocumentNameField = useCallback(() => {
    setDocumentNameValues((prev) => [...prev, ""]);
  }, []);

  const removeDocumentNameField = useCallback((index) => {
    setDocumentNameValues((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((_, idx) => idx !== index);
    });
  }, []);

  const handleLanguageFieldChange = useCallback(
    (changedPlaceholder, newValue) => {
      setFieldValues((prev) => {
        const nextValue = newValue || "";
        const updated = {
          ...prev,
          [changedPlaceholder]: nextValue,
        };

        const normalized = nextValue.trim().toLowerCase();
        if (!normalized || normalized === "english") {
          return updated;
        }

        const languageFields = (selectedDoc?.Fields || []).filter((f) => isLanguageField(f));
        const sibling = languageFields.find((f) => f.OriginalPlaceholder !== changedPlaceholder);
        if (!sibling) {
          return updated;
        }

        const siblingKey = sibling.OriginalPlaceholder;
        if (!updated[siblingKey]) {
          updated[siblingKey] = "English";
        }

        return updated;
      });
    },
    [isLanguageField, selectedDoc],
  );

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const documentNameField = (selectedDoc.Fields || []).find((f) => isDocumentNameField(f));
      const nonEmptyDocumentNames = documentNameValues
        .map((value) => String(value || "").trim())
        .filter(Boolean);

      if (documentNameField && nonEmptyDocumentNames.length === 0) {
        showSnackbar("Please add at least one Document Name", theme, "error");
        return;
      }

      if (documentNameField && hasDuplicateDocumentNames) {
        showSnackbar("Duplicate document names are not allowed", theme, "error");
        return;
      }

      const namesToGenerate = documentNameField ? nonEmptyDocumentNames : [null];

      for (let i = 0; i < namesToGenerate.length; i += 1) {
        const docName = namesToGenerate[i];
        const normalizedValues = {};
        (selectedDoc.Fields || []).forEach((f) => {
          const isDocName = documentNameField && f.OriginalPlaceholder === documentNameField.OriginalPlaceholder;
          const val = isDocName ? docName : fieldValues[f.OriginalPlaceholder];
          if (f.FieldType === "boolean") {
            normalizedValues[f.OriginalPlaceholder] = val ? "true" : "false";
          } else {
            normalizedValues[f.OriginalPlaceholder] = val ?? "";
          }
        });

        const generateResponse = await api.post(`/serviceDocuments/${selectedDoc.id}/generate`, {
          fieldValues: normalizedValues,
        });

        const { downloadUrl } = generateResponse.data;

        const fileResponse = await api.get(downloadUrl, { responseType: "blob" });
        const blob = fileResponse.data;
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;

        const baseFromInput = docName || selectedDoc?.Name || "Document";
        const docBaseName = String(baseFromInput).replace(/\.docx$/i, "").trim() || "Document";
        link.download = `COT_${docBaseName}.docx`;

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }

      showSnackbar(`${selectedDoc.Name} generated successfully`, theme, "success");
      handleBack();
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to generate document";
      showSnackbar(msg, theme, "error");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "80%", lg: "70%" },
          height: "auto",
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          maxHeight: { xs: "95%", sm: "75%" },
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            p: "0.75rem 2rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton color="primary" size="large" onClick={selectedDoc ? handleBack : closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {selectedDoc ? selectedDoc.Name : "Documents"}
            </Typography>
          </Box>
          {selectedDoc && (
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
              onClick={handleGenerate}
              disabled={generating}
              sx={{ borderRadius: "10px" }}
            >
              {generating ? "Generating..." : "Generate"}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "auto",
            maxHeight: {
              xs: "calc(95vh - 180px)",
              sm: "calc(75vh - 180px)",
            },
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
          }}
        >
          {(loading || loadingDoc) && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress />
            </Box>
          )}

          {error && !loading && (
            <Typography color="error" sx={{ p: 2 }}>
              {error}
            </Typography>
          )}

          {!loading && !loadingDoc && !selectedDoc && documents.length === 0 && (
            <Stack spacing={1} sx={{ p: 2 }}>
              <Typography color="textSecondary">
                No service documents available. Create one in Settings first.
              </Typography>
            </Stack>
          )}

          {!loading && !loadingDoc && !selectedDoc && documents.length > 0 && (
            <Stack spacing={2}>
              {documents.map((doc) => (
                <Card
                  key={doc.id}
                  elevation={0}
                  sx={{
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSelectDoc(doc)}
                    sx={{ borderRadius: "12px" }}
                  >
                    <CardContent sx={{ p: 2.25 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <DescriptionRoundedIcon
                          sx={{
                            color: theme.palette.primary.main,
                            mt: 0.2,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.75 }}
                          >
                            {doc.Name}
                          </Typography>
                          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                            {doc.Description || "No description provided"}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          )}

          {!loadingDoc && selectedDoc && (
            <Stack spacing={2.5}>
              {(selectedDoc.Fields || []).length === 0 && (
                <Typography color="textSecondary">
                  This document has no fields. Click Generate to create it.
                </Typography>
              )}
              {(selectedDoc.Fields || []).map((field) => {
                const value = fieldValues[field.OriginalPlaceholder] ?? "";
                if (field.FieldType === "boolean") {
                  return (
                    <FormControlLabel
                      key={field.id}
                      control={
                        <Checkbox
                          checked={!!value}
                          onChange={(e) =>
                            setFieldValues((prev) => ({
                              ...prev,
                              [field.OriginalPlaceholder]: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={field.UserFriendlyLabel + (field.IsRequired ? " *" : "")}
                    />
                  );
                }

                if (isLanguageField(field)) {
                  return (
                    <Autocomplete
                      key={field.id}
                      options={languages.map((language) => language.Name)}
                      value={value || null}
                      onChange={(_, newValue) => handleLanguageFieldChange(field.OriginalPlaceholder, newValue)}
                      autoHighlight
                      disablePortal
                      noOptionsText="No matching language"
                      slotProps={{
                        paper: {
                          sx: {
                            maxHeight: 260,
                          },
                        },
                        listbox: {
                          sx: {
                            maxHeight: 220,
                            py: 0.5,
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={field.UserFriendlyLabel}
                          required={field.IsRequired}
                          size="small"
                          fullWidth
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      )}
                    />
                  );
                }

                if (isDocumentNameField(field)) {
                  return (
                    <Stack key={field.id} spacing={1}>
                      {documentNameValues.map((nameValue, index) => {
                        const normalized = String(nameValue || "").trim().toLowerCase();
                        const isDuplicate = normalized && duplicateDocumentNameSet.has(normalized);
                        return (
                        <TextField
                          key={`${field.id}-${index}`}
                          label={index === 0 ? field.UserFriendlyLabel : `${field.UserFriendlyLabel} ${index + 1}`}
                          required={field.IsRequired}
                          value={nameValue}
                          onChange={(e) => handleDocumentNameValueChange(index, e.target.value)}
                          error={!!isDuplicate}
                          helperText={isDuplicate ? "Duplicate document name" : undefined}
                          size="small"
                          fullWidth
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {index === documentNameValues.length - 1 && (
                                    <IconButton size="small" color="primary" onClick={addDocumentNameField}>
                                      <AddRoundedIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {documentNameValues.length > 1 && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeDocumentNameField(index)}
                                    >
                                      <RemoveRoundedIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                        );
                      })}
                      {hasDuplicateDocumentNames && (
                        <Typography variant="body2" color="error">
                          Duplicate Document Name values are not allowed. Please make each name unique before generating.
                        </Typography>
                      )}
                    </Stack>
                  );
                }

                if (field.FieldType === "date") {
                  return (
                    <DatePicker
                      key={field.id}
                      label={field.UserFriendlyLabel}
                      value={value ? dayjs(value) : null}
                      onChange={(newValue) =>
                        setFieldValues((prev) => ({
                          ...prev,
                          [field.OriginalPlaceholder]: newValue ? newValue.format("YYYY-MM-DD") : "",
                        }))
                      }
                      slotProps={{
                        textField: {
                          required: field.IsRequired,
                          size: "small",
                          fullWidth: true,
                          sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px" } },
                        },
                      }}
                    />
                  );
                }

                return (
                  <TextField
                    key={field.id}
                    label={field.UserFriendlyLabel}
                    required={field.IsRequired}
                    type={field.FieldType === "number" ? "number" : "text"}
                    value={value}
                    onChange={(e) =>
                      setFieldValues((prev) => ({
                        ...prev,
                        [field.OriginalPlaceholder]: e.target.value,
                      }))
                    }
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TranslationDocuments;
